/**
 * <hydrate-slp-products> — Layer 3 consumer custom element.
 *
 * Renders product cards built from the APIM product-search result produced by
 * the existing hydrateSLP pipeline. Unlike a server-rendered Shopify product
 * grid, these cards come entirely from the NG API response (caption, image,
 * price, SKU) — no Liquid product loop is involved.
 *
 * Architecture position:
 *   Layer 1: query-product-list.js  (raw APIM fetch)
 *   Layer 2: hydrate-callers.js     (orchestration + `theme:search:hydrated` dispatch)
 *   Layer 3: hydrate-slp-products.js (this module — renders cards + pagination)
 *
 * Data source:
 *   - `window.__ngHydration` — the cached result, set by the auto-bootstrap in
 *     snippets/scripts.liquid once hydrateSLP resolves (race-condition guard).
 *   - `theme:search:products:hydrated` CustomEvent — the eager per-module
 *     event dispatched the moment the product search resolves, so this module
 *     paints without waiting for the store search (MA-1367).
 *   - `theme:search:hydrated` CustomEvent on `document` — the combined event
 *     dispatched by hydrate-callers.js once every query resolves (re-fetches,
 *     filter changes). An envelope already rendered from the eager event is
 *     skipped by identity.
 *
 * Module order (MA-1367):
 *   The page-template API's `moduleOrder` decides whether this module renders
 *   at all — a stores-only order hides the section entirely (the partner
 *   stores module owns the page), and an empty order (the API's zero-results
 *   template) reveals the zero-results empty state.
 *
 * Pagination:
 *   The APIM response includes a `pagination: { page, totalPages }` object.
 *   When totalPages > 1, navigation controls are rendered into the
 *   `[data-hsp-pagination]` nav element using the shared hydrate-pagination
 *   module. Page changes dispatch a cancelable `theme:page:change` event so
 *   the filters section (when present) can own the re-fetch while preserving
 *   filter+sort state. When no filters section handles the event, this module
 *   falls back to a direct `hydrateSLP` call.
 *
 * On a search page with no `?q=` query the APIM call returns an error envelope.
 * That is expected: outside of the theme editor the results grid hides and the
 * section's zero-results empty state (message box, category browse, and
 * recommendations carousel) is revealed instead.
 */

import { renderPagination, bindPaginationEvents, readPaginationLabels, scrollToWithHeaderOffset } from '@theme/hydrate-pagination';
import { hydrateSLP, FILTER_AGGREGATIONS } from '@theme/hydrate-callers';
import { renderProductCard, renderProductCardSkeletons, initCardInteractions } from '@theme/ng-product-card';
import { interleaveBanners, matchBrandBanners } from '@theme/inline-banner';

/**
 * @typedef {Object} APIMHit
 * @property {string} [shopifyProductId]
 * @property {string} [shopifyProductHandle]
 * @property {string} [catalogSku]
 * @property {string} [storeName]
 * @property {{ caption?: string, image?: { url?: string }, priceInfo?: unknown }} [product]
 */

class HydrateSlpProducts extends HTMLElement {
  /** @type {((event: Event) => void) | null} */
  #onHydrated = null;

  /** Envelope rendered by the eager per-module event — the combined
   * `theme:search:hydrated` event re-delivers the same object and is
   * skipped by identity to avoid a redundant re-render.
   * @type {Object | null} */
  #lastRenderedEnvelope = null;

  /** @type {number} */
  #currentPage = 1;

  /** @type {number} */
  #totalPages = 0;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {((event: Event) => void) | null} */
  #onViewChange = null;

  /** @type {((event: Event) => void) | null} */
  #onLoading = null;

  connectedCallback() {
    this.maxProducts = parseInt(this.getAttribute('data-max-products') || '30', 10);

    // Read initial page from URL so ?page=N shareable links work.
    this.#currentPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10) || 1;

    this.#onHydrated = (event) => {
      const detail = /** @type {CustomEvent} */ (event).detail;
      this.#render(detail);
    };
    // Eager per-module event (products resolved — stores may still be in
    // flight) plus the combined event (re-fetches, filter changes, race guard).
    document.addEventListener('theme:search:products:hydrated', this.#onHydrated);
    document.addEventListener('theme:search:hydrated', this.#onHydrated);

    // Paint skeletons for the in-flight window of every SLP search (initial
    // load and each filter / sort / page re-fetch) until results resolve.
    this.#onLoading = (event) => {
      if (/** @type {CustomEvent} */ (event).detail?.context === 'slp') this.#showLoading();
    };
    document.addEventListener('theme:search:loading', this.#onLoading);

    // Bind pagination click events (delegated listener on the nav element).
    bindPaginationEvents(
      this.querySelector('[data-hsp-pagination]'),
      (page) => this.#onPageChange(page)
    );

    // Mobile grid/list view toggle (controls mobile column count).
    this.#initViewToggle();

    // Race guard: if hydrateSLP resolved before this element connected, the
    // event has already fired and been missed — process the cached result now.
    // With no cached result the fetch is still in flight — keep the loading
    // status visible and wait for `theme:search:hydrated`; revealing the
    // zero-results state here would flash it before real results arrive.
    // (Even a search with no `?q=` resolves with an error envelope, so the
    // event always fires.) In the theme editor there is no APIM data — show
    // the placeholder so merchants can find the section.
    if (window.__ngProductHydration) {
      this.#render(window.__ngProductHydration);
    } else if (window.__ngHydration) {
      this.#render(window.__ngHydration);
    } else if (this.#isDesignMode) {
      this.#showEmptyState();
    } else {
      // Fetch still in flight (and the loading event may have fired before this
      // element connected) — paint skeletons until results resolve.
      this.#showLoading();
    }
  }

  disconnectedCallback() {
    if (this.#onHydrated) {
      document.removeEventListener('theme:search:products:hydrated', this.#onHydrated);
      document.removeEventListener('theme:search:hydrated', this.#onHydrated);
    }

    if (this.#onLoading) {
      document.removeEventListener('theme:search:loading', this.#onLoading);
    }

    if (this.#onViewChange) {
      document.removeEventListener('theme:view:change', this.#onViewChange);
    }

    if (this.#abortController) {
      this.#abortController.abort();
    }
  }

  /**
   * Applies the persisted grid/list view on connect and keeps it in sync with
   * the mobile toggle that lives in the filter bar. The toggle dispatches a
   * `theme:view:change` event (and writes localStorage); this section just
   * reflects the chosen view on its grid container, which survives card
   * re-renders so it only needs setting on connect and on each change.
   */
  #initViewToggle() {
    this.#applyView(this.#storedView());

    this.#onViewChange = (event) => {
      const view = /** @type {CustomEvent} */ (event).detail?.view === 'list' ? 'list' : 'grid';
      this.#applyView(view);
    };
    document.addEventListener('theme:view:change', this.#onViewChange);
  }

  /** @returns {'grid' | 'list'} Persisted view preference, defaulting to grid. */
  #storedView() {
    try {
      return localStorage.getItem('ng:product-view') === 'list' ? 'list' : 'grid';
    } catch (error) {
      return 'grid';
    }
  }

  /**
   * Reflects a view on the grid container (CSS switches mobile columns).
   * @param {'grid' | 'list'} view
   */
  #applyView(view) {
    const grid = this.#grid;
    if (grid) grid.setAttribute('data-view', view);
  }

  get #grid() {
    return this.querySelector('[data-hsp-grid]');
  }

  get #status() {
    return this.querySelector('[data-hsp-status]');
  }

  get #paginationNav() {
    return this.querySelector('[data-hsp-pagination]');
  }

  get #emptyState() {
    return this.querySelector('[data-slp-empty-state]');
  }

  get #results() {
    return this.querySelector('[data-slp-results]');
  }

  get #relatedSearchMessage() {
    return this.querySelector('[data-slp-related-search-message]');
  }

  get #relatedSearchText() {
    return this.querySelector('[data-slp-related-search-text]');
  }

  /** Whether the storefront is being viewed in the Shopify theme editor. */
  get #isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  /** @returns {number} Number of products after which to insert a banner. */
  get #bannerInsertAfter() {
    return parseInt(this.getAttribute('data-banner-insert-after') || '0', 10) || 0;
  }

  /** @returns {boolean} Whether SRP banner interleaving is enabled. */
  get #srpBannersEnabled() {
    return this.hasAttribute('data-enable-srp-banners');
  }

  /**
   * Parses the SRP banner JSON data embedded by the Liquid snippet.
   * @returns {import('@theme/inline-banner').InlineBanner[]}
   */
  get #srpBannerData() {
    const script = this.querySelector('script[data-srp-banners]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return [];
    }
  }

  /**
   * Renders product cards from a hydration result. Only acts on SLP-context
   * results; non-SLP contexts (typeahead/plp) are ignored.
   *
   * @param {{ context?: string, products?: { ok?: boolean, data?: { products?: APIMHit[], pagination?: { page?: number, totalPages?: number } }, error?: { message?: string } } } | null | undefined} result
   */
  #render(result) {
    if (!result || result.context !== 'slp') return;

    // The page-template API can exclude this module entirely. A stores-only
    // order hides the section (the partner stores module owns the page); an
    // empty order is the API's zero-results template — reveal the empty state
    // as the applicable user feedback.
    const pageTemplate = /** @type {Record<string, any>} */ (result).pageTemplate;
    const moduleOrder = pageTemplate?.ok && Array.isArray(pageTemplate.data?.moduleOrder)
      ? pageTemplate.data.moduleOrder
      : null;

    if (moduleOrder && !moduleOrder.includes('products')) {
      if (moduleOrder.includes('stores')) {
        this.#hideModule();
      } else {
        this.#showEmptyState();
      }
      return;
    }

    const envelope = result.products;

    // Combined event re-delivering the envelope already rendered from the
    // eager per-module event — nothing new to paint.
    if (envelope && envelope === this.#lastRenderedEnvelope) return;

    if (!envelope || !envelope.ok) {
      this.#handleNoProducts(result, moduleOrder);
      return;
    }

    // Hits render as-is, in API order. A hit whose retail price is missing or
    // flagged `shouldDisplay: false` still renders — renderProductCard's
    // invalid-price gate shows it without a price.
    const hits = Array.isArray(envelope.data?.products) ? envelope.data.products : [];

    if (hits.length === 0) {
      this.#handleNoProducts(result, moduleOrder);
      return;
    }

    this.#lastRenderedEnvelope = envelope;

    // Related-search banner — when the API fell back to related products
    // because the original query had zero exact matches, show a contextual
    // message above the grid with the search term.
    const isRelatedSearch = Boolean(
      envelope.data?.messages?.relatedSearchApplied?.isZeroResultOnOriginalSearch
    );
    const relatedMsg = this.#relatedSearchMessage;
    if (relatedMsg) {
      if (isRelatedSearch) {
        // Write into the dedicated text span so the leading icon is preserved.
        // Fall back to the wrapper itself if the span isn't present.
        const textEl = this.#relatedSearchText || relatedMsg;
        const template = textEl.dataset.template || relatedMsg.dataset.template;
        const query = new URLSearchParams(window.location.search).get('q') || '';
        if (query && template) {
          // Render the search term in a bold <strong> while keeping the
          // surrounding copy at the regular weight. Built from text nodes so
          // the query string is never interpreted as HTML.
          const [before = '', after = ''] = template.split('[[terms]]');
          const term = document.createElement('strong');
          term.className = 'tw:font-bold';
          term.textContent = query;
          textEl.replaceChildren(before, term, after);
        }
        relatedMsg.hidden = false;
      } else {
        relatedMsg.hidden = true;
      }
    }

    // Successful render — hide empty state, show results. In the theme editor
    // the empty-state section manages its own visibility so merchants can
    // always find and configure it.
    if (!this.#isDesignMode) {
      const emptyState = this.#emptyState;
      if (emptyState) emptyState.hidden = true;
    }
    const results = this.#results;
    if (results) results.hidden = false;

    // Extract pagination metadata from the APIM response.
    const pagination = envelope.data?.pagination;
    if (pagination) {
      this.#currentPage = pagination.page || 1;
      this.#totalPages = pagination.totalPages || 1;
    }

    const grid = this.#grid;
    if (!grid) return;

    // Render ALL hits from the response — the API returns the correct
    // page-sized batch; no client-side slicing needed.
    const allowAtc = this.hasAttribute('data-allow-atc');
    const cardHtmlArray = hits.map((hit) => renderProductCard(hit, { allowAtc }));

    const insertAfter = this.#bannerInsertAfter;
    let banners = [];

    if (this.#srpBannersEnabled && insertAfter > 0) {
      const allBanners = this.#srpBannerData;
      banners = matchBrandBanners(hits, allBanners);
    }

    grid.innerHTML = (banners.length > 0 && insertAfter > 0)
      ? interleaveBanners(cardHtmlArray, banners, insertAfter)
      : cardHtmlArray.join('');

    // Attach delegated card interaction handlers (wishlist, swatches, sizes).
    initCardInteractions(grid);

    // Clear the loading state now that real cards are in place.
    grid.removeAttribute('aria-busy');

    const status = this.#status;
    if (status) status.hidden = true;

    // Render pagination controls.
    const paginationNav = this.#paginationNav;
    if (paginationNav) {
      paginationNav.innerHTML = renderPagination(
        this.#currentPage,
        this.#totalPages,
        readPaginationLabels(paginationNav)
      );
      paginationNav.hidden = this.#totalPages <= 1;
    }

    this.hidden = false;
  }

  /**
   * Handles a pagination page-change request. Updates the URL, dispatches
   * a cancelable `theme:page:change` event (so the filters section can own
   * the re-fetch when present), and falls back to a direct hydrateSLP call
   * when no other handler prevents the default.
   *
   * @param {number} page - 1-based target page number.
   */
  async #onPageChange(page) {
    this.#currentPage = page;

    // Update URL for shareability — remove `page` param when on page 1.
    const url = new URL(window.location.href);
    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }
    history.replaceState(null, '', url.toString());

    // Dispatch cancelable event — the filters section calls preventDefault()
    // when it handles the re-fetch (preserving filter + sort state).
    const event = new CustomEvent('theme:page:change', {
      detail: { page, context: 'slp' },
      cancelable: true,
      bubbles: true,
    });
    document.dispatchEvent(event);

    if (!event.defaultPrevented) {
      // No filters section on the page — do a direct re-fetch.
      if (this.#abortController) {
        this.#abortController.abort();
      }
      this.#abortController = new AbortController();

      const query = new URLSearchParams(window.location.search).get('q') || '';
      const hasFilters = Boolean(document.querySelector('hydrate-filters'));
      const productParams = hasFilters ? { aggregations: FILTER_AGGREGATIONS } : {};

      try {
        const result = await hydrateSLP({
          query,
          page,
          productSize: this.maxProducts,
          productParams,
          signal: this.#abortController.signal,
        });
        window.__ngHydration = result;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        console.error('[slp-products] page change failed', error);
      }
    }

    // Scroll to the top of this section after page change, offsetting the
    // sticky header so the first product isn't hidden behind it.
    scrollToWithHeaderOffset(this);
  }

  /**
   * Hides the module entirely — used when the page-template `moduleOrder`
   * excludes products (stores-only template). The stores module owns the page
   * in that state, so neither the results grid nor the empty state may show.
   */
  #hideModule() {
    this.#lastRenderedEnvelope = null;
    this.hidden = true;
  }

  /**
   * Avoids showing the global zero-results state while a requested store
   * search is pending or has returned matches. The empty state is shown only
   * after every requested module is known to have no results.
   *
   * @param {Record<string, any>} result
   * @param {Array<string> | null} moduleOrder
   */
  #handleNoProducts(result, moduleOrder) {
    const storesRequested = moduleOrder === null || moduleOrder.includes('stores');
    if (storesRequested) {
      if (result.stores === undefined) {
        this.#hideModule();
        return;
      }

      const stores = result.stores;
      if (stores?.ok && stores.data?.totalCount > 0) {
        this.#hideModule();
        return;
      }
    }

    this.#showEmptyState();
  }

  /**
   * Shows the zero-results empty state (server-rendered tips box, category
   * carousel, and recommendations carousel). Hides the results container and
   * populates the "no results for …" text from the current URL `?q=` param.
   *
   * In the theme editor there is no APIM data, so the empty state renders
   * visible server-side (see the section's Liquid) — here we just hide the
   * results container so merchants see the empty state alone.
   */
  #showEmptyState() {
    if (this.#isDesignMode) {
      this.hidden = false;
      const results = this.#results;
      if (results) results.hidden = true;
      return;
    }

    // Hide the results grid, show the empty state.
    const results = this.#results;
    if (results) results.hidden = true;

    const emptyState = this.#emptyState;
    if (emptyState) {
      emptyState.hidden = false;

      // Populate the "no results for …" line with the search query. The store
      // name (the country store, e.g. shop.name) is baked into the template
      // server-side by Liquid.
      const noResultsText = emptyState.querySelector('[data-slp-no-results-text]');
      if (noResultsText) {
        const query = new URLSearchParams(window.location.search).get('q') || '';
        if (query && noResultsText.dataset.template) {
          noResultsText.textContent = noResultsText.dataset.template.replace('[[terms]]', query);
          noResultsText.hidden = false;
        }
      }

      // The category Swiper may have initialized while this container was
      // hidden (all widths 0) — a resize event makes it re-measure now that
      // the empty state is visible.
      window.dispatchEvent(new Event('resize'));
    }

    this.hidden = false;
  }

  /**
   * Paints skeleton cards into the results grid while an SLP search is in
   * flight — keeps the section visible, hides the zero-results empty state,
   * and marks the grid `aria-busy`. Replaced by real cards (or the empty
   * state) once the search resolves. Skipped in the theme editor, which has no
   * APIM data and manages the empty state server-side.
   */
  #showLoading() {
    if (this.#isDesignMode) return;

    const grid = this.#grid;
    if (!grid) return;

    this.hidden = false;

    const emptyState = this.#emptyState;
    if (emptyState) emptyState.hidden = true;

    const results = this.#results;
    if (results) results.hidden = false;

    const status = this.#status;
    if (status) status.hidden = true;

    const paginationNav = this.#paginationNav;
    if (paginationNav) paginationNav.hidden = true;

    const count = Math.min(this.maxProducts || 8, 12);
    grid.setAttribute('aria-busy', 'true');
    grid.innerHTML = renderProductCardSkeletons(count);
  }

}

if (!customElements.get('hydrate-slp-products')) {
  customElements.define('hydrate-slp-products', HydrateSlpProducts);
}
